/**
 * Theme application (Roadmap P4-A — "apply the chosen theme").
 *
 * The Settings → Preferences theme picker (Light / Dark / System) used to save a
 * preference that nothing ever applied — the app shipped a full `.dark` stylesheet
 * and `dark:` utilities that never activated. This is the one place that actually
 * toggles the `.dark` class on <html>, which (via the `@custom-variant dark` in
 * globals.css) drives both the Tailwind `dark:` utilities and the hand-written
 * `.dark` rules together.
 *
 * Theme is a DEVICE-level preference (stored in localStorage under THEME_KEY) so it
 * applies instantly at boot with no flash — see the inline bootstrap in index.html,
 * which reads the SAME key. Settings mirrors the choice into org_config for
 * cross-device continuity and calls setTheme() for the live toggle.
 *
 * Default is 'system' → follow the OS, and live-update when the OS flips.
 */

export type ThemePref = 'light' | 'dark' | 'system';

export const THEME_KEY = 'karr_theme';

const isDark = (pref: ThemePref): boolean =>
    pref === 'dark' ||
    (pref === 'system' &&
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);

/** Read the stored device theme, defaulting to 'system'. */
export function getStoredTheme(): ThemePref {
    try {
        const v = localStorage.getItem(THEME_KEY);
        if (v === 'light' || v === 'dark' || v === 'system') return v;
    } catch { /* ignore */ }
    return 'system';
}

/** Toggle the `.dark` class on <html> to match the given preference. */
export function applyTheme(pref: ThemePref): void {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', isDark(pref));
}

/** Fired on window by setTheme(), so every switch on screen shows the new choice. */
export const THEME_EVENT = 'karr:theme';

/** Persist the device theme and apply it immediately (live, app-wide). */
export function setTheme(pref: ThemePref): void {
    try { localStorage.setItem(THEME_KEY, pref); } catch { /* ignore */ }
    applyTheme(pref);
    // themebtn: the header switch and Settings both show the choice, so each
    // hears when the other one makes it.
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(THEME_EVENT));
}

/** Whether the screen is dark right now (what 'system' resolved to included). */
export function isDarkNow(): boolean {
    return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

let inited = false;

/**
 * Apply the stored theme once at startup and keep 'system' in sync with the OS.
 * Idempotent — safe to call more than once.
 */
export function initTheme(): void {
    if (inited || typeof window === 'undefined') return;
    inited = true;
    applyTheme(getStoredTheme());
    // When following the OS, re-apply as the OS light/dark preference changes.
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (getStoredTheme() === 'system') applyTheme('system');
    });
}
